import {
  CategoryForBet,
  GeneralTypeOfBet,
  DetailedTypeOfBet,
  PlayerProp,
  SportsbookType,
  BetProductWithSportsbook,
} from '@pff-consumer/schema'
import { MAX_BET_LIMIT, formatToTime, getTotalBetsCartLength } from '@pff-consumer/utils'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { LayoutAnimation } from 'react-native'
import { consumerApi } from '../consumer-api/consumer-api'

export const BETTING_KEY = 'betting'
export const BETTING_CART_KEY = 'bettingCartItems'

export type BettingCartItem = BetProductWithSportsbook | PlayerProp

export interface BettingCartState {
  betsCart: BettingCartItem[]
  showBetsTrackerView: boolean
  betsCartLength: number
  sportsbookBetsCartLength: Record<SportsbookType, number>
  showBetsCartScreen: boolean
  showBetLimitModal: boolean
  enableEventsLockout: boolean
  selectedEventId: number | undefined
  sportsbookId: SportsbookType
}

export const initialBettingCartState: BettingCartState = {
  betsCart: [],
  showBetsTrackerView: false,
  betsCartLength: 0,
  sportsbookBetsCartLength: { BETMGM: 0, FD: 0 },
  showBetsCartScreen: false,
  showBetLimitModal: false,
  enableEventsLockout: true,
  selectedEventId: undefined,
  sportsbookId: 'FD',
}

export const bettingSlice = createSlice({
  name: BETTING_KEY,
  initialState: initialBettingCartState,
  reducers: {
    addToBetsCart: (state, { payload }: { payload: BettingCartItem }) => {
      if (state.betsCart.length >= MAX_BET_LIMIT) {
        state.showBetLimitModal = true
      } else {
        state.betsCart.push(payload)
        state.sportsbookBetsCartLength[payload.sportsbookId] += 1
        if (getTotalBetsCartLength(state.sportsbookBetsCartLength) === 1) {
          LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)
          state.showBetsTrackerView = true
        }
      }
    },
    removeFromBetsCart: (state, { payload }: { payload: BettingCartItem }) => {
      const filteredCart = state.betsCart.filter((cartItem) => cartItem.sportsbookBetId !== payload.sportsbookBetId)
      state.betsCart = filteredCart

      if (state.sportsbookBetsCartLength[payload.sportsbookId] > 0) {
        state.sportsbookBetsCartLength[payload.sportsbookId] -= 1
      }

      if (getTotalBetsCartLength(state.sportsbookBetsCartLength) === 0) {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)
        state.showBetsTrackerView = false
        state.showBetsCartScreen = false
      }
    },
    clearAllBets: (state) => {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)
      state.betsCart = []
      state.sportsbookBetsCartLength = initialBettingCartState.sportsbookBetsCartLength
      state.showBetsCartScreen = false
      state.showBetsTrackerView = false
    },
    clearBetsCartForSportsbook: (state, { payload }: { payload: SportsbookType }) => {
      const filteredCart = state.betsCart.filter((cartItem) => cartItem.sportsbookId !== payload)
      state.betsCart = filteredCart
      state.sportsbookBetsCartLength[payload] = 0
      if (getTotalBetsCartLength(state.sportsbookBetsCartLength) === 0) {
        state.showBetsTrackerView = false
        state.showBetsCartScreen = false
      }
    },
    openBetsCartScreen: (state) => {
      state.showBetsCartScreen = true
    },
    closeBetsCartScreen: (state) => {
      state.showBetsCartScreen = false
    },
    closeBetLimitModal: (state) => {
      state.showBetLimitModal = false
    },
    setEventsLockoutState: (state, { payload }: { payload: boolean }) => {
      state.enableEventsLockout = payload
    },
    setSportsbookState: (state, { payload }: { payload: SportsbookType }) => {
      state.sportsbookId = payload
      if (getTotalBetsCartLength(state.sportsbookBetsCartLength) === 0) {
        state.showBetsTrackerView = false
      } else {
        state.showBetsTrackerView = true
      }
    },
    setSelectedEventId: (state, { payload }: { payload: number | undefined }) => {
      state.selectedEventId = payload
    },
  },
})

export const bettingReducer = bettingSlice.reducer
export const {
  addToBetsCart,
  removeFromBetsCart,
  clearAllBets,
  clearBetsCartForSportsbook,
  openBetsCartScreen,
  closeBetsCartScreen,
  closeBetLimitModal,
  setEventsLockoutState,
  setSportsbookState,
  setSelectedEventId,
} = bettingSlice.actions

export const getBettingCartState = (rootState: { [x: string]: BettingCartState }): BettingCartState => {
  return rootState[BETTING_KEY]
}

const getGamesState = consumerApi.endpoints.getSDKEvents.select({ sportsbookId: initialBettingCartState.sportsbookId })
const getTeamsState = consumerApi.endpoints.getTeams.select()

const cartTeamCountHelper = (
  cartTeamCount: { [x: string]: { count: number; home: number; away: number } },
  teamName: string,
  isAwayTeam: boolean
) => {
  if (!cartTeamCount[teamName]) {
    // eslint-disable-next-line no-param-reassign
    cartTeamCount[teamName] = { count: 1, home: isAwayTeam ? 0 : 1, away: isAwayTeam ? 1 : 0 }
  } else {
    // eslint-disable-next-line no-param-reassign
    cartTeamCount[teamName] = {
      count: cartTeamCount[teamName].count + 1,
      home: isAwayTeam ? cartTeamCount[teamName].home : cartTeamCount[teamName].home + 1,
      away: isAwayTeam ? cartTeamCount[teamName].away + 1 : cartTeamCount[teamName].away,
    }
  }
  return cartTeamCount
}

export const bettingSelectors = {
  getBetsCartState: createSelector(getBettingCartState, (state: BettingCartState) => state),
  getBetSlipCount: createSelector(getBettingCartState, (state: BettingCartState) =>
    getTotalBetsCartLength(state.sportsbookBetsCartLength)
  ),
  getCartBetCategoryCount: createSelector(getBettingCartState, (state: BettingCartState) => {
    const cartBetCategoryCount: { [key in CategoryForBet]: number } = {
      'Game Bet': 0,
      'General Player Prop': 0,
      'Mismatches Prop': 0,
    }
    state.betsCart.forEach((bet) => {
      if ('category' in bet && bet.category) {
        cartBetCategoryCount[bet.category] += 1
      } else if ('propKey' in bet) {
        if ('mismatchId' in bet) {
          cartBetCategoryCount['Mismatches Prop'] += 1
        } else {
          cartBetCategoryCount['General Player Prop'] += 1
        }
      }
    })
    return cartBetCategoryCount
  }),
  getCartDetailedBetTypeCount: createSelector(getBettingCartState, (state: BettingCartState) => {
    const cartBetTypeCount: { [key in DetailedTypeOfBet]: number } = {
      AwayTeamMoneyline: 0,
      AwayTeamSpread: 0,
      HomeTeamMoneyline: 0,
      HomeTeamSpread: 0,
      Over: 0,
      Under: 0,
      Player: 0,
      rush_att: 0,
      rush_yd: 0,
      rush_recv_yd: 0,
      pass_rush_yd: 0,
      pass_comp: 0,
      pass_int: 0,
      pass_att: 0,
      pass_yd: 0,
      pass_td: 0,
      recv_yd: 0,
      recv_rec: 0,
      longest_pass: 0,
      longest_reception: 0,
      kick_points: 0,
      any_time_td: 0,
      first_td: 0,
      two_plus_td: 0,
      three_plus_td: 0,
      last_td: 0,
      first_half_td: 0,
      second_half_td: 0,
      first_td_second_half: 0,
      first_completion: 0,
      first_reception: 0,
      longest_rush: 0,
      first_rush_att: 0,
      sack: 0,
      tackle_assist: 0,
      alt_recv_yd: 0,
      alt_pass_yd: 0,
      alt_rush_yd: 0,
      most_pass_yd: 0,
      most_rush_yd: 0,
      first_team_td_scorer: 0,
    }
    state.betsCart.forEach((bet) => {
      if ('detailedType' in bet && bet.detailedType) {
        cartBetTypeCount[bet.detailedType] += 1
      } else if ('propKey' in bet && bet.propKey) {
        cartBetTypeCount[bet.propKey] += 1
      }
    })
    return cartBetTypeCount
  }),
  getCartGeneralBetTypeCount: createSelector(getBettingCartState, (state: BettingCartState) => {
    const cartBetTypeCount: { [key in GeneralTypeOfBet]: number } = {
      Spread: 0,
      Moneyline: 0,
      Total: 0,
      'Player Prop': 0,
    }
    state.betsCart.forEach((bet) => {
      if ('generalType' in bet && bet.generalType) {
        // eslint-disable-next-line no-plusplus
        cartBetTypeCount[bet.generalType]++
      }
    })
    return cartBetTypeCount
  }),
  getCartGameIdCount: createSelector(getBettingCartState, (state: BettingCartState) => {
    const cartGameIdCount: { [gameId: number]: number } = {}
    state.betsCart.forEach((bet) => {
      const { gameId } = bet
      if (gameId) {
        if (!cartGameIdCount[gameId]) {
          cartGameIdCount[gameId] = 1
        } else {
          // eslint-disable-next-line no-plusplus
          cartGameIdCount[gameId]++
        }
      }
    })
    return cartGameIdCount
  }),
  getCartTeamCount: createSelector(
    getBettingCartState,
    getGamesState,
    getTeamsState,
    (bettingState, gamesState, teamsState) => {
      const games = gamesState.data
      const teams = teamsState.data
      const cartTeamCount: { [team: string]: { count: number; home: number; away: number } } = {}
      bettingState.betsCart.forEach((bet) => {
        const { gameId } = bet
        if (gameId) {
          const gameData = games?.find((game) => game.gameId === gameId)
          if (gameData) {
            const { homeTeamId, awayTeamId } = gameData
            const homeTeam = teams?.find((team) => team.teamId === homeTeamId)
            const homeTeamName = homeTeam?.fullName || ''
            const awayTeam = teams?.find((team) => team.teamId === awayTeamId)
            const awayTeamName = awayTeam?.fullName || ''
            cartTeamCountHelper(cartTeamCount, awayTeamName, true)
            cartTeamCountHelper(cartTeamCount, homeTeamName, false)
          }
        }
      })
      return cartTeamCount
    }
  ),
  getCartGameTimesCount: createSelector(getBettingCartState, getGamesState, (bettingState, gamesState) => {
    const games = gamesState.data
    const cartGameWeekCount: { [gameTimes: string]: number } = {}
    bettingState.betsCart.forEach((bet) => {
      const { gameId } = bet
      if (gameId) {
        const gameData = games?.find((game) => game.gameId === gameId)
        if (gameData) {
          const { startTimestamp } = gameData
          if (startTimestamp) {
            const timeDisplay = formatToTime(startTimestamp)
            if (!cartGameWeekCount[timeDisplay]) {
              cartGameWeekCount[timeDisplay] = 1
            } else {
              // eslint-disable-next-line no-plusplus
              cartGameWeekCount[timeDisplay]++
            }
          }
        }
      }
    })
    return cartGameWeekCount
  }),
  getLockedEventsState: createSelector(getBettingCartState, (state: BettingCartState) => {
    return state.enableEventsLockout
  }),
  getSelectedEventId: createSelector(getBettingCartState, (state) => state.selectedEventId),
  getSportsbookId: createSelector(getBettingCartState, (state) => state.sportsbookId),
}
